import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";

const TARGET = new Date("2027-01-29T09:00:00+05:30").getTime();
const SITE = "https://www.startupmahabazaar.com";

const daysLeft = () => Math.max(0, Math.ceil((TARGET - Date.now()) / 86400000));

const loadImg = (src) =>
    new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = src;
    });

const L = {
    w: 1080, h: 1920, emblemH: 180, emblemY: 150,
    startupY: 430, startupSize: 30,
    nameY: 548, nameSize: 96,
    yearY: 650, yearSize: 46,
    tagY: 732, tagSize: 39,
    divY: 792,
    numY: 1080, numSize: 215,
    labelY: 1160, labelSize: 27,
    dateY: 1292, dateSize: 24,
    siteY: 1430, siteSize: 21,
    archTop: 90, archW: 800, archBottom: 1560,
};

async function drawCard(canvas) {
    const { w: W, h: H } = L;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    await Promise.all([
        document.fonts.load('600 90px "Fraunces"'),
        document.fonts.load('italic 400 30px "Fraunces"'),
        document.fonts.load('500 22px "Jost"'),
        document.fonts.load('300 30px "Jost"'),
    ]);
    const emblem = await loadImg("/emblem.png");
    const cx = W / 2;
    const canSpace = "letterSpacing" in ctx;
    const setSpacing = (px) => {
        if (canSpace) ctx.letterSpacing = `${px}px`;
    };

    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#1C060C");
    bg.addColorStop(0.55, "#140409");
    bg.addColorStop(1, "#26070F");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = "rgba(201,162,75,0.05)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 44) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = 0; y <= H; y += 44) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }

    const glow = ctx.createRadialGradient(cx, H * 0.32, 10, cx, H * 0.32, W * 0.42);
    glow.addColorStop(0, "rgba(165,28,48,0.4)");
    glow.addColorStop(1, "rgba(165,28,48,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    const arch = (w, color, lw) => {
        ctx.beginPath();
        ctx.moveTo(cx - w / 2, L.archBottom);
        ctx.lineTo(cx - w / 2, L.archTop + w / 2);
        ctx.arc(cx, L.archTop + w / 2, w / 2, Math.PI, 0);
        ctx.lineTo(cx + w / 2, L.archBottom);
        ctx.strokeStyle = color;
        ctx.lineWidth = lw;
        ctx.stroke();
    };
    arch(L.archW, "rgba(201,162,75,0.5)", 2);
    arch(L.archW - 34, "rgba(201,162,75,0.22)", 1);
    ctx.strokeStyle = "rgba(201,162,75,0.4)";
    ctx.setLineDash([5, 7]);
    ctx.beginPath();
    ctx.moveTo(cx - L.archW / 2 - 60, L.archBottom);
    ctx.lineTo(cx + L.archW / 2 + 60, L.archBottom);
    ctx.stroke();
    ctx.setLineDash([]);

    const ew = (emblem.width / emblem.height) * L.emblemH;
    ctx.drawImage(emblem, cx - ew / 2, L.emblemY, ew, L.emblemH);

    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const goldGrad = ctx.createLinearGradient(cx - 280, 0, cx + 280, 0);
    goldGrad.addColorStop(0, "#8C6A2F");
    goldGrad.addColorStop(0.28, "#E8CF8F");
    goldGrad.addColorStop(0.52, "#C9A24B");
    goldGrad.addColorStop(0.76, "#F2E2B0");
    goldGrad.addColorStop(1, "#8C6A2F");

    setSpacing(Math.round(L.startupSize * 0.55));
    ctx.font = `500 ${L.startupSize}px "Jost"`;
    ctx.fillStyle = "#C9A24B";
    ctx.fillText("STARTUP", cx, L.startupY);

    setSpacing(Math.round(L.nameSize * 0.06));
    ctx.font = `600 ${L.nameSize}px "Fraunces"`;
    ctx.fillStyle = goldGrad;
    ctx.fillText("MAHABAZAAR", cx, L.nameY);

    setSpacing(Math.round(L.yearSize * 0.5));
    ctx.font = `300 ${L.yearSize}px "Jost"`;
    ctx.fillStyle = "#F3EAD8";
    ctx.fillText("2027", cx, L.yearY);

    setSpacing(0);
    ctx.font = `italic 400 ${L.tagSize}px "Fraunces"`;
    ctx.fillStyle = "rgba(232,207,143,0.92)";
    ctx.fillText("Bazaar of Ideas, Innovation & Impact", cx, L.tagY);

    ctx.strokeStyle = "rgba(201,162,75,0.45)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(cx - 190, L.divY); ctx.lineTo(cx - 16, L.divY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx + 16, L.divY); ctx.lineTo(cx + 190, L.divY); ctx.stroke();
    ctx.save();
    ctx.translate(cx, L.divY);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = "#C9A24B";
    ctx.fillRect(-4, -4, 8, 8);
    ctx.restore();

    ctx.font = `600 ${L.numSize}px "Fraunces"`;
    ctx.fillStyle = goldGrad;
    ctx.fillText(String(daysLeft()), cx, L.numY);

    setSpacing(Math.round(L.labelSize * 0.5));
    ctx.font = `500 ${L.labelSize}px "Jost"`;
    ctx.fillStyle = "rgba(179,164,147,0.95)";
    ctx.fillText("DAYS TO GO", cx, L.labelY);

    setSpacing(Math.round(L.dateSize * 0.32));
    ctx.font = `400 ${L.dateSize}px "Jost"`;
    ctx.fillStyle = "rgba(243,234,216,0.85)";
    ctx.fillText("29–31 JANUARY 2027 · MSIT & MSI, NEW DELHI", cx, L.dateY);

    setSpacing(Math.round(L.siteSize * 0.3));
    ctx.font = `500 ${L.siteSize}px "Jost"`;
    ctx.fillStyle = "#C9A24B";
    ctx.fillText("startupmahabazaar.com", cx, L.siteY);
    setSpacing(0);

    ctx.strokeStyle = "rgba(201,162,75,0.35)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(14, 14, W - 28, H - 28);
}

export default function ShareCardButton({ label = "Share the Countdown", className = "" }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        drawCard(canvasRef.current).catch(() => alive && toast.error("Could not render the card right now."));
        document.body.style.overflow = "hidden";
        window.__lenis?.stop();
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => {
            alive = false;
            document.body.style.overflow = "";
            window.__lenis?.start();
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const caption = `The countdown is on — ${daysLeft()} days to Startup MahaBazaar 2027, India's Grand Festival of Startups, Innovation & Entrepreneurship. 29–31 January 2027 · MSIT & MSI, New Delhi. ${SITE}`;

    const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

    const download = async () => {
        const blob = await toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mahabazaar-2027-countdown-story.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Countdown card downloaded — share it everywhere.");
    };

    const shareNative = async () => {
        setBusy(true);
        try {
            const blob = await toBlob();
            const file = new File([blob], "mahabazaar-2027-countdown.png", { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: "Startup MahaBazaar 2027", text: caption });
            } else if (navigator.share) {
                await navigator.share({ title: "Startup MahaBazaar 2027", text: caption });
            } else {
                await download();
            }
        } catch {
            /* user dismissed the share sheet */
        } finally {
            setBusy(false);
        }
    };

    const copyCaption = async () => {
        try {
            await navigator.clipboard.writeText(caption);
            toast.success("Caption copied — paste it with your card.");
        } catch {
            try {
                const ta = document.createElement("textarea");
                ta.value = caption;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                ta.remove();
                toast.success("Caption copied — paste it with your card.");
            } catch {
                toast.error("Could not copy right now.");
            }
        }
    };

    return (
        <>
            <button onClick={() => setOpen(true)} data-testid="share-card-open-btn"
                className={`inline-flex items-center gap-2 text-[0.64rem] tracking-[0.28em] uppercase text-[var(--champagne)]/80 hover:text-[var(--champagne)] transition-colors ${className}`}>
                <Share2 size={14} strokeWidth={1.5} /> {label}
            </button>

            {open && createPortal(
                <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" data-testid="share-card-modal">
                    <div className="absolute inset-0 bg-[#100206]/92 backdrop-blur-md" onClick={() => setOpen(false)} />
                    <div className="relative card-luxe w-full sm:max-w-md max-h-[92vh] flex flex-col shadow-2xl">
                        <div className="flex items-start justify-between gap-4 p-6 pb-0">
                            <div>
                                <p className="text-[0.58rem] tracking-[0.36em] uppercase text-[var(--gold)]">Share Card · Story 1080×1920</p>
                                <h3 className="font-display text-2xl text-[var(--ivory)] mt-2">
                                    {daysLeft()} Days to the <span className="gold-text">Bazaar</span>
                                </h3>
                            </div>
                            <button onClick={() => setOpen(false)} aria-label="Close share card" data-testid="share-card-close"
                                className="w-9 h-9 border hairline flex items-center justify-center text-[var(--muted)] hover:text-[var(--champagne)] transition-colors shrink-0">
                                <X size={15} />
                            </button>
                        </div>

                        <div className="px-6 py-4 overflow-y-auto min-h-0">
                            <div className="relative mx-auto w-full max-w-[210px] aspect-[9/16] border hairline bg-[#1C060C]">
                                <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" data-testid="share-card-canvas" />
                            </div>
                            <p className="mt-4 text-[0.62rem] tracking-[0.14em] uppercase text-muted-luxe/80 text-center">
                                Perfect for Instagram &amp; WhatsApp stories
                            </p>
                            <div className="mt-3 border hairline p-3.5 bg-[rgba(201,162,75,0.03)]">
                                <p className="text-[0.55rem] tracking-[0.3em] uppercase text-[var(--gold)] mb-1.5">Caption</p>
                                <p className="text-xs text-[var(--ivory)]/70 leading-relaxed" data-testid="share-caption-preview">{caption}</p>
                            </div>
                        </div>

                        <div className="p-6 pt-4 border-t hairline grid grid-cols-1 gap-2.5 bg-[#140409]/60">
                            <button onClick={shareNative} disabled={busy} data-testid="share-native-btn" className="btn btn-gold !py-3.5 w-full">
                                <Share2 size={13} /> {busy ? "Preparing…" : "Share to Social"}
                            </button>
                            <div className="grid grid-cols-2 gap-2.5">
                                <button onClick={download} data-testid="share-download-btn" className="btn btn-outline !py-3 !px-3">
                                    <Download size={13} /> Save PNG
                                </button>
                                <button onClick={copyCaption} data-testid="share-copy-btn" className="btn btn-dark !py-3 !px-3">
                                    <Link2 size={13} /> Copy Caption
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
